/**
 Author - Brijesh Pandey
 Git - https://bitbucket.org/__brijesh/
 **/

import React from "react";
import SafeLink from "components/commons/SafeLink";
import Head from "next/head";

import styled from "styled-components";
import {WealthyEnv} from "helpers";
import GlobalStyle from "static/next/css/PartnerGlobalStyle";
import {
  LogoWrapper,
  LogoImage
} from "components/StaticPages/Common";
import {NewFooter} from "components";

import {
  SegmentAnalytics
} from "components";

const CDN_URL  = WealthyEnv.HOMEPAGE_CDN_URL;

const databaseCredentialsCodingPolicy = `${CDN_URL}/privacy-database-credentials-policy.pdf`;
const webappSecurityPolicy = `${CDN_URL}/privacy-application-security-pdf.pdf`;
const informationLoggingStandard = `${CDN_URL}/privacy-information-logging-standard-pdf.pdf`;

const Privacy = () => {
  return (
     <>
       <Head>
         <title>Wealthy | Privacy Policy </title>
         <meta  content="Wealthy | Privacy Policy" name="title" />
         <meta content="Visit Wealthy to know about Privacy Policy." name="description" />
         <meta content="Wealth Management Jobs, Relationship Management Jobs, Private Banker Jobs, Portfolio Manager Jobs, Investment Manager Jobs" name="keywords" />
         <link href={`${WealthyEnv.URL}/privacy`} rel="canonical" />
         <link href="https://i.wlycdn.com/wealthy-home-page/new-fav-icon.png" rel="icon" />
         <link as="style" href="static/next/css/header-footer.css" rel="preload" />
         <link href="static/next/css/header-footer.css" rel="stylesheet" />
       </Head>
       <SegmentAnalytics />
       <GlobalStyle />
       <LogoWrapper>
         <SafeLink href={WealthyEnv.URL}>
           <LogoImage
             alt="Wealthy"
             loading="lazy"
             src={`${WealthyEnv.HOMEPAGE_CDN_URL}/wealthy-new-purple-logo.svg`}
           />
         </SafeLink>
       </LogoWrapper>
       <Wrapper>
         <div className="privacy">
           <div className="page-container wl-primary-text">

             {/* Section: Privacy Policy */}
             <div className="privacy-policy">
               <h1><b>Privacy Policy</b></h1>
               <p>
                In the course of using this website or any of the websites under the ‘Wealthy.in’ domain or availing the products and services vide the online application forms and questionnaires, online consents and such other details required from time to time on any of Wealthy’s (and/or its affiliates) web platforms or mobile applications, Wealthy and/or its affiliates may become privy to some of your personal information, including which may or may not be of confidential nature. Wealthy is strongly committed to protecting the privacy of its users/clients and has taken all necessary and reasonable measures to protect the confidentiality of any customer information.&nbsp;
               </p>
               <p>
                For the purpose of these Privacy Policy, wherever the context so mentions “Covered Persons”, "Client","You" or "Your”, it shall mean any natural or legal person who has visited this website/platform and/or has agreed to or has enquired open an account and/or initiated the process of opening an account with Wealthy(also defined as “Covered Persons”).&nbsp;
               </p>
               <p>
                Wealthy allows any person to use/visit/browse the website without registering on the website. WealthyIN Customer Services Pvt Ltd, Buildwealth Technologies Private Limited and WealthyIN Broking Private Limited will be referred to together as Wealthy in this policy. Access to Your Wealthy account and the usage of Your account is subject to Your compliance with all the terms and conditions set forth herein, read along with all documents, including but not limited to; applications forms and undertakings, signed by you during account opening, and any terms/consents/policies included on Wealthy’s website/domain, i.e. <br/>
                <SafeLink href="https://wealthy.in/" rel="noopener noreferrer" target="_blank" style={{ color: 'blue' }}>https://wealthy.in/</SafeLink>, including its <SafeLink href="https://www.wealthy.in/terms-and-conditions" rel="noopener noreferrer" target="_blank" style={{ color: 'blue' }}>“Terms and Conditions”</SafeLink>, <SafeLink href="https://www.wealthy.in/policies-and-procedures" rel="noopener noreferrer" target="_blank" style={{ color: 'blue' }}>“Policies & Procedures”</SafeLink>, and <SafeLink href="https://www.wealthy.in/investor-awareness" rel="noopener noreferrer" target="_blank" style={{ color: 'blue' }}>“Disclosure”</SafeLink>. <br/>
                If you have any questions or concerns regarding this privacy policy, you should contact our Customer Support Desk at customer.support@wealthy.in 

               </p>
             </div>

             {/* Section: Overview*/}
             <div className="overview">
               <h2><b>1. OVERVIEW</b></h2>
               <p>
                 We commit to respecting your online privacy data. We further recognize your need for appropriate protection and management of any personally identifiable information ("Personal Information") you share with us. Information that is considered personal about you by us includes, but is not limited to, your name, address, email address, portfolio details or other contact information.
               </p>
               <p>
                 In order to use this website, you are required to register yourself by providing the following information which includes, but is not limited to:
               </p>
               <ul>
                 <li>1. Name</li>
                 <li>2. PAN Number</li>
                 <li>3. Phone number</li>
                 <li>4. Email address</li>
                 <li>5. Date of Birth</li>
                 <li>6. Bank account details</li>
                 <li>7. Pictures of self and documents required for KYC.</li>
               </ul>
               <p><br /></p>
               <p>
                 If you are using our portfolio tracker services, then you also choose to share your consolidated investment statement.
               </p>
               <p><br /></p>
             </div>

             {/* Section: NOTIFICATION OF MODIFICATIONS AND CHANGES TO THE T&C AND PRIVACY POLICY */}
             <div className="notification">
               <h2>
                 <b>2. NOTIFICATION OF MODIFICATIONS AND CHANGES TO THE T&amp;C AND PRIVACY POLICY</b>
               </h2>
               <p>
                 We reserve the right to change the Terms and Privacy Policy from time to time as seen fit, without any intimation to you, and your continued use of the site will signify your acceptance of any amendment to these terms.
               </p>
               <p>
                 You are therefore advised to re-read the Terms of Service on a regular basis. Should it be that you do not accept any of the modifications or amendments to the Terms, you may terminate your use of this website immediately.
               </p>
             </div>
             {/* Section: INFORMATION WE COLLECT */}
             <div className="information">
               <h2><b>3. INFORMATION WE COLLECT</b></h2>
               <p>
                 Our site's online requisition form requires users to give us contact information (like your name, address, telephone number and email address), PAN card information, bank account information, and other Know-your-customer information (like your picture, PAN Card copy, address proof copy). As a registered member you are required to provide a valid email address at registration and choose a username or alias that represents your identity on our website.
               </p>
               <p>
                 Certain information, such as your name, e-mail addresses, is collected in order to, among other things, verify your identity and for use as account numbers in our record system. Email addresses, Geo-locations, any information entered in the Live Chat window or any other form displayed on any section of the website are also collected.
               </p>
               <h4>Connecting Your Email Account</h4>
               <p>
                 You can choose to provide explicit consent to connect your email accounts with your Wealthy account.
               </p>
               <p>
                 Once connected, Wealthy will securely access and analyze the contents of emails from registrar &amp; transfer agents and depositories for the purpose of reading the consolidated holdings statement to provide a consolidated view of your investment portfolio at one place.
               </p>
               <p>
                 We encourage you to review your information prior to signing in through the applicable service.
               </p>
               <p>
                 You can choose to enable us to access one or more of your email accounts by connecting such account(s) with your Wealthy account. If you connect your email account(s), Wealthy will automatically access and analyze the contents of emails (only from two kind of sources - RTAs and Depositories) on an ongoing basis for the purpose of identifying documents related to the applicable App Services availed by you, including the portfolio tracker. This is to assist the user in collating details on the App, which will primarily consist of include investment details.
               </p>
               <p>
                 Once linked, the applicable information will then be extracted from your emails into your Wealthy account so that you may access the same through the Wealthy website/app.
               </p>
               <p>
                 Wealthy’s access to the email account(s) is authorized through the email provider’s access mechanism. If you choose to use our portfolio tracker, Wealthy will securely store details for each of your financial investments, including your sign-in user name and authorisation tokens for tracked accounts. This information will be used to enable Wealthy to automatically access your consolidated investment holding statement and analyze, extract, and store information securely from such accounts for use in the Wealthy website/app.
               </p>
               <p>
                 You will be asked in each case whether you want to connect your email account.
               </p>
               <p>
                 You may manage your connections to email and de-link the same at any time from your Google account settings.Click&nbsp;
                 <SafeLink
                   href="https://myaccount.google.com/permissions"rel="noreferrer" target="_blank"
                 >
                   <b>here</b>
                 </SafeLink>
                 &nbsp;to access Google Mail’s permission settings.
               </p>
             </div>
             {/* Section: COOKIES */}
             <div className="cookies">
               <h2><b>4. COOKIES</b></h2>
               <p>
                 We use data collection devices such as "cookies" on certain pages of our Websites. "Cookies" are small files sited on your hard drive that assist us in providing customized services. We also offer certain features that are only available through the use of a "cookie". Cookies can also help us provide information, which is targeted to your interests.Cookies may be used whether you register with us or not.&nbsp;
               </p>
             </div>

             {/* Section: EXTERNAL LINKS ON WEBSITE */}
             <div className="external-links">
               <h2><b>5. EXTERNAL LINKS ON WEBSITE</b></h2>
               <p>
                 The Website may include hyperlinks to other web sites or content or resources including without limitation websites of Developers. We have no control over any websites or resources, which are provided by companies or persons other than us.
               </p>
               <p>
                 You acknowledge and agree that we are not responsible for the availability of any such external sites or resources, and do not endorse any advertising, products or other materials available on or from such websites or resources.
               </p>
               <p>
                 You acknowledge and agree that We are not liable for any loss or damage which may be incurred by you as a result of the availability of those external sites or resources, or as a result of any reliance placed by you on the completeness, accuracy or existence of any advertising, or other materials on, or available from, such websites or resources. These third-party service providers and Third-Party Sites may have their own privacy policies governing the storage and retention of your personal information that you may be subject to. We recommend that when you enter a Third-Party Site, you review the Third Party Siteâ€™s privacy policy as it relates to safeguarding your personal information. We use third-party advertising companies to serve ads when you visit the Website.
               </p>
             </div>

             {/* Section: OUR USE OF YOUR INFORMATION */}
             <div className="information-usage">
               <h2><b>6. OUR USE OF YOUR INFORMATION</b></h2>
               <p>
                 By signing up to the Wealthy platform, you agree and consent to receive all communications at the mobile number provided, even if this mobile number is registered under DND/NCPR list under TRAI regulations. And for that purpose, you further authorise Buildwealth Technologies Pvt. Ltd. to share/disclose the information to any third party service provider or any affiliates, group companies, their authorised agents, or third-party service providers.
               </p>
               <p>
                 Your contact information is also used to contact you when necessary. We use your IP address to help diagnose problems with our server, and to administer our Website. Your IP address is also used to help identify you and to record your transaction on www.wealthy.in for regulatory compliance. Finally, we may use your IP address to help protect our partners and ourselves from fraud. We will continue to enhance our security procedures as new technology becomes available. We will transfer information about you if we are acquired by or merged with another company.
               </p>
               <h4>Use of Personal Information</h4>
               <p>
                 The following sections describe in more detail how we propose to use your personal information:
               </p>
               <h4>Fulfilling your Transaction Request</h4>
               <p>
                 Any information provided by you will be used solely for processing your transaction or for any other purposes for which you have granted us the access to such information, based on our mutual agreement / discussions / communications / correspondences. All personal information provided by you in our website/app which has been duly received and accepted by us will be used solely for the purpose of processing a transaction on our platform. To enable such process, we may share information with authorised third-parties, for instance, our business partners, financial teams/institutions, or postal/government authorities involved in fulfilment of said transactions, if any. In connection with a transaction, we may also contact you as part of our customer satisfaction surveys or for market research purposes.
               </p>
               <h4>Personalising your Experience on our website/app</h4>
               <p>
                 We may use information we collect about you to provide you with a personalised experience on our website/app, such as providing you with content based on your interest and making navigation on our sites easier.
               </p>
               <p>
                 As you access and use Wealthy, we may request access wherever applicable:
               </p>
               <table>
                 <thead>
                   <tr>
                     <td style={{width: 50}}>Sr No</td>
                     <td style={{width: 100}}>Permission</td>
                     <td>Purpose</td>
                   </tr>
                 </thead>
                 <tbody>
                   <tr>
                     <td>1</td>
                     <td>SMS</td>
                     <td>Required for improving signup process, for OTP verification. We do not read any SMS other than our OTP SMS.</td>
                   </tr>
                   <tr>
                     <td>2</td>
                     <td>Location</td>
                     <td>Personalisation of the offerings like connecting to Wealthy’s partner for in-person meeting.</td>
                   </tr>
                   <tr>
                     <td>3</td>
                     <td>Contact</td>
                     <td>For marketing offers like Referral program.</td>
                   </tr>
                   <tr>
                     <td>4</td>
                     <td>Email</td>
                     <td>Reading your consolidated investment holding statement to build a consolidated view of your investments. We do not read any e-mails other than the statement emails.</td>
                   </tr>
                 </tbody>
               </table>
             </div>

             {/* Section: OUR DISCLOSURE OF YOUR INFORMATION */}
             <div className="disclosure">
               <h2><b>7. OUR DISCLOSURE OF YOUR INFORMATION</b></h2>
               <p>
                 Due to the existing regulatory environment, we cannot ensure that all of your private communications and other personally identifiable information will never be disclosed in ways not otherwise described in this Privacy Policy. By way of example (without limiting and foregoing), we may be forced to disclose information to the government, law enforcement agencies or third parties. Under certain circumstances, third parties may unlawfully intercept or access transmissions or private communications, or members may abuse or misuse your information that they collect from our Website. Therefore, although we use bank-grade security practices to protect your privacy, we do not promise, and you should not expect, that your personally identifiable information or private communications would always remain private.
               </p>
               <p>
                 As a matter of policy, we do not sell or rent any personally identifiable information about you to any third party. However, the following describes some of the ways that your personally identifiable information may be disclosed.
               </p>
               <p>
                 <b><i>7.1External Service Providers:</i></b>
                 There may be a number of services offered by external service providers that help you use our Websites. If you choose to use these optional services, and in the course of doing so, disclose information to the external service providers, and/or grant them permission to collect information about you, then their use of your information is governed by their private policy.&nbsp;
               </p>
               <p>
                 <b><i>7.2 Other Corporate Entities:</i></b>
                 We share some data mainly personally identifiable information about you, with our parent and/or subsidiaries,  Such data will be shared for the sole purpose of enhancing  the performance of your investments. To the extent that these entities have access to your information, they will treat it at least as protectively as they treat information they obtain from their other members. It is possible that We and/or our subsidiaries, or any combination of such, could merge with or be acquired by another business entity. Should such a combination occur, you should expect that we would share some or all of your information in order to continue to provide the service. You will receive notice of such event (to the extent that it occurs).&nbsp;
               </p>
               <p>
                 <b><i>7.3 Law and Order:</i></b>
                 We cooperate with law enforcement inquiries, as well as other third parties to enforce laws, such as: intellectual property rights, fraud and other rights. We can (and you authorize us to) disclose any information about you to law enforcement and other government officials as we, in our sole discretion, believe necessary or appropriate, in connection with an investigation of fraud, intellectual property infringements, or other activity that is illegal or may expose us or you to legal liability.&nbsp;
               </p>
             </div>

             {/* Section: ACCESSING, REVIEWING AND CHANGING/DELETING YOUR PROFILE/ACCOUNT */}
             <div className="modification">
               <h2><b>8. ACCESSING, REVIEWING AND CHANGING/DELETING YOUR PROFILE/ACCOUNT</b></h2>
               <p>
                 Following registration, you can review and change the information you submitted until you make a successful investment. If you change any information we may keep track of your old information. After making your investment, you can change your bank account details, correspondence address, nominee details after providing sufficient and adequate proof of the authenticity of information.
               </p>
               <p>
                 We will retain in our files information you have requested to remove for certain circumstances, such as to resolve disputes, troubleshoot problems and enforce our terms and conditions. Further, such prior information is never completely removed from our databases due to technical and legal constraints, including stored 'back up' systems. Therefore, you should not expect that all of your personally identifiable information will be completely removed from our databases in response to your requests.
               </p>
               <br />
               <p>
                 <b> <i>8.1 Account Deletion : </i></b>
                 We provide all our Users an option to request the deletion of a the complete account through the support section on the Wealthy App.<br /><br />Following such request, we delete all such information related to the specific account including but not limited to profile information, transaction details, reward details, referrals details, statement details, google oauth sessions etc. that we are not required to retain.<br /><br />In certain circumstances, we may be unable to delete your account, such as if there is any outstanding dispute or unresolved claims pending on your account. However, upon resolution of the issue preventing deletion, the information is immediately deleted and can’t be recovered thereafter. Please note that we may retain certain information if necessary for our own legitimate business interests such as fraud prevention and enhancing users’ safety and security OR to fulfil our legal obligations and compliance.<br />
               </p>
             </div>

             {/* Section: CONTROL OF YOUR PASSWORD */}
             <div className="password-control">
               <h2><b>9. CONTROL OF YOUR PASSWORD</b></h2>
               <p>
                 When you sign up to become a Member, you will also be asked to choose a password. You are entirely responsible for maintaining the confidentiality of your password. It is important that you protect it against unauthorized access of your account and information by choosing your password carefully, and keeping your password and computer secure by signing out after using our services
               </p>
               <p>
                 You agree not to use the account, username, email address or password of another Member at any time or to disclose your password to any third party. If you choose to share this information with third parties to provide you additional services, you are responsible for all actions taken with your login information and password and therefore should review each third party's privacy policy. You are responsible for all actions taken with your login information and password, including fees. If you lose control of your password, you may lose substantial control over your personally identifiable information and may be subject to legally binding actions taken on your behalf. Therefore, if your password has been compromised for any reason, you should immediately change your password. You agree to notify us immediately if you suspect any consistent unauthorized use of your account or access to your password even after changing it.
               </p>
             </div>

             {/* Section: OTHER INFORMATION COLLECTORS */}
             <div className="information-collectors">
               <h2><b>10. OTHER INFORMATION COLLECTORS</b></h2>
               <p>
                 Except as otherwise expressly included in this Privacy Policy, this document only addresses the use and disclosure of information we collect from you. To the extent that you disclose your information to other parties, whether they are on our Websites or on other sites throughout the Internet, different rules may apply to their use or disclosure of the information you disclose to them. To the extent that we use third party advertisers, they adhere to their own privacy policies. Since we do not control the privacy policies of the third parties, you are subject to ask questions before you disclose your personal information to others.
               </p>
             </div>

             {/* Section: Security */}
             <div className="security">
               <h2><b>11. SECURITY</b></h2>
               <p>
                 We treat data as an asset that must be protected against loss and unauthorized access. We employ bank-grade security techniques to protect such data from unauthorized access by members inside and outside the company.We follow generally accepted industry standards to protect the personal information submitted to us, when we receive it. However, "perfect security" does not exist on the Internet. You therefore agree that any security breaches beyond the control of our standard security procedures are at your sole risk and discretion.
               </p>
               <p>
                 If you wish to read more on how we ensure security of users information, click on the following links -
               </p>
               <p>
                 <SafeLink
                   href={databaseCredentialsCodingPolicy}
                   rel="noopener noreferrer"
                   target="_blank"
                 >
                   Database Credential Credential Policy
                 </SafeLink>
               </p>
               <p>
                 <SafeLink
                   href={informationLoggingStandard}
                   rel="noopener noreferrer"
                   target="_blank"
                 >
                   Information Logging Standard Policy
                 </SafeLink>
               </p>
               <p>
                 <SafeLink
                   href={webappSecurityPolicy}
                   rel="noopener noreferrer"
                   target="_blank"
                 >
                   Webapp Security Policy
                 </SafeLink>
               </p>
             </div>

             {/* Section: Broking
             <div className="broking">
               <h2><b>12. BROKING POLICY</b></h2>
               <p>
                 We at&nbsp;
                 <SafeLink
                   href="www.wealthy.in"
                   target="_blank"
                 >
                   Wealthy
                 </SafeLink>
                 &nbsp;acknowledge and accept that the personal details that you impart to us, is to be kept in strict confidentiality and to use the information only in the manner which would be beneficial to our customers. We consider our relationship with you as invaluable and strive to respect and safeguard your right to privacy.We shall protect the personal details received from you with the same degree of care, but no less than a reasonable degree of care, to prevent the unauthorized use, dissemination, or publication of this information as we protect our own confidential information of a like nature.We shall use the personal information to improve our service to you and to keep you updated about our new product or information that may be of interest to you.
                 <br /><br />
                 The information collected from you would be used in the right spirit and context in which it is intended to be used. Your information would be used by us to process your trading request and to carry out the settlements of your obligations.We would ensure that we collect personal information only to the extent it is necessary to administer our services in the best possible manner and what is required under the various regulations of Indian Laws. To ensure high quality services and high degree of value addition to you, we may combine the information given by you on the web or through other channels.Under certain circumstances we may be required to share the information given by you with the third parties, where we feel they can contribute to add value and improve the quality of services imparted by us to you. We shall take all reasonable steps to ensure that the confidentiality of your information is maintained by imposing strict confidentiality standards on all the third parties with whom we part these information. In all circumstances we shall ensure that your personal information is protected by strict confidentiality agreement. We shall not allow any third parties to retain your personal information longer than what is warranted by the nature of services renderedWe would also impart your personal information wherever it is required to be disclosed under law to any of the governmental agency or regulatory bodies.
                 <br /><br />
                 We ensure that the personal information given to us by you on the web is placed in the secured portion of our web-site. We use the most advanced encryption technology, Secure Socket Layer (SSL), to ensure that the information transmitted between you and us across the Internet is safe and cannot be accessed by any outsider. SSL has been universally accepted on the World Wide Web for authenticated and encrypted communication between clients and servers. To ensure security of access to the personal information and transaction details, the access to the secured zone of our website is restricted by the unique login ID and Password selected by you. You should be very careful in handling the ID and password and you should ensure that you do not reveal it to anybody, nor do you keep it in writing. You should keep changing your password periodicallyTo ensure safety of your trading access, our technology automatically logs you out of the site if no activity is registered for 30 minutes. However you should ensure that each time you leave your terminal you log yourself out. This prevents someone else from accessing your account if you leave your computer and your session has not "timed out" WealthyIN makes no guarantees or representations as to, and shall have no liability for, any electronic content delivered by any third party, including, without limitation, the accuracy, subject matter, quality or timeliness of any electronic content on the Website. WealthyIN shall not be responsible for the contents of any linked site or any link contained in a linked site, or any changes or updates to such sites. The links being provided to the Customer are only for convenience, and the inclusion of any link does not imply endorsement by WealthyIN of the site, or its content or the sponsoring organization.
                 <br /><br />
                 By providing access to other websites, WealthyIN is not recommending the purchase or sale of the securities issued by it nor is it an endorsement of services provided by its sponsoring organization.To enable us to serve you better, it is necessary that your personal information available with us is updated and accurate. Our web-site enables you to update your personal information. You can also send us an email at Customer Service and we shall ensure that your information is updated and protected from any misuse or unauthorized revelation.

               </p>
             </div> */}
             <div className="consent">
               <h2><b>12. Consent of Communications</b></h2>
               <p>
               By accepting this Privacy Policy, you consent to receiving all communications from Wealthy, including transactional, service-related, and promotional updates.&nbsp;
               </p>
             </div>


             {/* Section: Disclaimer */}
             <div className="disclaimer">
               <h2><b>13. DISCLAIMER</b></h2>
               <p>
                We cannot ensure that all of your private communications and other personal information (including sensitive information like credit card information and bank account number) will never be disclosed in ways not otherwise described in this Privacy Policy. Therefore, although we are committed to protecting your privacy, we do not promise, and you should not expect, that your personal information will always remain private. As a user of the Site, you understand and agree that you assume all responsibility and risk for your use of the Site, the internet generally, and the documents you post or access and for your conduct on and off the Site.&nbsp;
               </p>
             </div>

             <div id="google-compliance">
               <h2>
                 <b>GOOGLE COMPLIANCE</b>
               </h2>
               <p>
                Wealthy&apos;s use and transfer to any other app of information received from Google Accounts will adhere to <SafeLink href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" rel="noopener noreferrer" target="_blank">Google API Services User Data Policy</SafeLink>, including the Limited Use requirements.
               </p>
             </div>
           </div>
         </div>
       </Wrapper>
       <NewFooter />
       <style jsx>
         {`
        .page-container p {
          margin: 0 0 15px;
          color: #3c3c3c;
          font-family: Avenir-Light;
          font-weight: 400;
          font-size: 18px;
          line-height: 26px;
          letter-spacing: .3px;
        }
        a {
          color: black;
          font-size: 18px;
          text-decoration: underline;
        }
        .page-container {
          margin: auto;
          padding: 2em 5% 6em;
          max-width: 950px;
        }
        .wl-primary-text {
          color: #3c3c3c;
          font-family: "Avenir-Book";
          font-size: 18px;
          font-weight: 300;
        }
        .page-container h1 {
          font-family: "Avenir-Heavy";
          padding-bottom: .25em;
          line-height: normal;
          color: rgba(0,0,0,.8);
          border-bottom: 1px solid #ccc;
          font-size: 2em;
          margin-bottom: 35px;
        }
        .page-container h2 {
          font-family: "Avenir-Heavy";
          font-weight: 500;
          line-height: normal;
          color: rgba(0,0,0,.8);
          margin-top: 40px;
          font-size: 1.5em;
          margin-bottom: 10px;
        }
        b {
          font-weight: 700;
        }
        h4 {
          margin-top: 10px;
          margin-bottom: 10px;
          font-size: 18px;
          font-family: inherit;
          font-weight: 500;
          line-height: 1.1;
        }
        @media (max-width : 768px) {
          .page-container p {
            margin: 0 0 10px;
            color: #3c3c3c;
            font-family: Avenir-Light;
            font-weight: 400;
            font-size: 16px;
            line-height: 26px;
            letter-spacing: .3px;
          }
          a {
            font-size: 16px;
          }
        }
      `}
       </style>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: 768px){
    display: flex;
    flex-direction: column;
    width: 100%;
  }
`;

export default Privacy;
